import React, { useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVerticalIcon } from 'lucide-react';
import { ownerDisplay, phaseLabel, statusLabel, stepTone, STATUS_PICKER, type ProjectStep, type StepDraft, type StepStatus } from '../../utils/projectSteps';
import { dateRange } from '../../utils/clientDates';
import { StepForm } from './StepForm';
import { BUTTON_QUIET, OPTION } from './adminStyles';

/**
 * One step in the studio's project tracker. Its own file since 14 Oct 2026 —
 * the combined panel was into the size range this editor silently truncates.
 *
 * **Dragged by the grip only.** `dragListener={false}` plus `touch-none` on
 * the handle, so a thumb on a phone moves the row and not the page. Same
 * pattern as `DeliverableAdmin`.
 *
 * **Status is a select on the row, not a field in the form.** It is the one
 * thing on a tracker that changes weekly, and a status that costs opening an
 * editor is a status nobody keeps current — the same disease that left
 * Recent Activity empty from the day it was built.
 *
 * **The stage leads the meta line.** The client page groups by it, so after a
 * row is dragged it is the one thing a staff member cannot otherwise see
 * without opening the editor.
 */
export function StepRow({
  step,
  clientName,
  editing,
  confirming,
  busy,
  onLift,
  onDrop,
  onStatus,
  onEdit,
  onCancelEdit,
  onSave,
  onAskRemove,
  onCancelRemove,
  onRemove
}: {
  step: ProjectStep;
  clientName: string;
  editing: boolean;
  confirming: boolean;
  busy: boolean;
  onLift: () => void;
  onDrop: () => void;
  onStatus: (status: StepStatus) => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (draft: StepDraft) => void;
  onAskRemove: () => void;
  onCancelRemove: () => void;
  onRemove: () => void;
}) {
  const dragControls = useDragControls();
  const [carrying, setCarrying] = useState(false);
  const tone = stepTone(step);
  const quiet = tone === 'done';
  const attention = tone === 'attention';
  return <Reorder.Item value={step} dragListener={false} dragControls={dragControls} onDragStart={() => {
    setCarrying(true);
    onLift();
  }} onDragEnd={() => {
    setCarrying(false);
    onDrop();
  }} animate={carrying ? {
    backgroundColor: 'rgba(255,255,255,0.05)',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)'
  } : {
    backgroundColor: 'rgba(255,255,255,0)',
    boxShadow: '0 0 0 rgba(0,0,0,0)'
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} style={{
    position: 'relative',
    zIndex: carrying ? 30 : 0
  }} className={['border-b border-ruleDark py-6', carrying ? 'cursor-grabbing rounded-[2px] ring-1 ring-paper/25' : ''].join(' ')} data-id="element-1541" data-mp="2a76cpzkh7ssd">
      {editing ? <div className="px-3" data-id="element-1542" data-mp="3btf8upsswy6a">
          <StepForm initial={{
        title: step.title,
        note: step.note,
        owner: step.owner,
        ownerName: step.ownerName,
        phase: step.phase,
        start: step.start,
        end: step.end
      }} busy={busy} submitLabel="Save Step" onCancel={onCancelEdit} onSubmit={onSave} data-id="element-1543" data-mp="3gubizl3qxgk5" />
        </div> : <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3 px-3" data-id="element-1544" data-mp="r9b2l9l3g9c3">
          <div className="flex min-w-0 items-baseline gap-5" data-id="element-1545" data-mp="1nx7kchs9paow">
            <span role="button" tabIndex={0} onPointerDown={event => {
          event.preventDefault();
          dragControls.start(event);
        }} aria-label={`Reorder ${step.title}`} title="Drag to reorder" className="shrink-0 translate-y-1 touch-none cursor-grab text-paper/30 transition-colors duration-150 ease-out hover:text-paper/70 active:cursor-grabbing" data-id="element-1546" data-mp="1jvvtk8xnqjf4">
              <GripVerticalIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-1547" data-mp="1mezazcz9pdf8" />
            </span>

            <div className="min-w-0" data-id="element-1548" data-mp="1rebxtxq52kht">
              <p className={`m-0 text-[1.05rem] leading-snug ${quiet ? 'text-paper/40' : 'text-paper'}`} data-id="element-1549" data-mp="1rfd9o4ezbb0l">
                {step.title}
              </p>
              <p className={`label-sm mt-2 m-0 ${attention ? 'text-paper/70' : 'text-paper/40'}`} data-id="element-1550" data-mp="1id41vmb3hvqw">
                {[phaseLabel(step.phase), dateRange(step.start, step.end) || 'To be scheduled', ownerDisplay(step, clientName), statusLabel(step.status, clientName)].filter(Boolean).join(' · ')}
              </p>
              {step.note && <p className="mt-2 m-0 max-w-xl text-[0.9rem] leading-relaxed text-paper/35" data-id="element-1551" data-mp="2p2m1eh5kpgb1">
                  {step.note}
                </p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-id="element-1552" data-mp="337unduwmom6w">
            <label className="shrink-0" data-id="element-1553" data-mp="3361e07umrnv7">
              <span className="sr-only" data-id="element-1554" data-mp="2z6smzaqz643c">Status for {step.title}</span>
              <select value={step.status} onChange={event => onStatus(event.target.value as StepStatus)} className={['label border bg-ink px-3 py-2 transition-colors duration-150 ease-out', step.status === 'done' ? 'border-paper/25 text-paper/40' : attention ? 'border-paper text-paper' : 'border-paper/30 text-paper/70 hover:border-paper/60'].join(' ')} data-id="element-1555" data-mp="2z4g7d48ow5tg">
                {STATUS_PICKER.map(option => <option key={option.value} value={option.value} className={OPTION} data-id="element-1556" data-mp="9gyqv5hjhjjd">
                    {option.label}
                  </option>)}
              </select>
            </label>

            <button type="button" onClick={onEdit} className={BUTTON_QUIET} data-id="element-1557" data-mp="citrubzzakum">
              Edit
            </button>

            {confirming ? <>
                <button type="button" onClick={onRemove} disabled={busy} className="label text-paper transition-opacity duration-150 ease-out hover:opacity-70" data-id="element-1558" data-mp="gm8azbj7rhca">
                  Really remove
                </button>
                <button type="button" onClick={onCancelRemove} className={BUTTON_QUIET} data-id="element-1559" data-mp="3oy44gtnujh46">
                  Keep
                </button>
              </> : <button type="button" onClick={onAskRemove} className="label text-paper/25 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-1560" data-mp="2r5xa284knox">
                Remove
              </button>}
          </div>
        </div>}
    </Reorder.Item>;
}